/**
 * Live relay controller (task-11).
 *
 * Bridges the generic `RelayClient` (WS transport + E2E + seq/outbox) to the
 * typed protocol payloads used by the app in live mode:
 *
 * - `approval.request`  → `onApproval(view)`
 * - `approval.decide`   ← sealed & signed by `decideApproval()` (delivery result
 *                          reported back through `onDelivery`)
 * - `chat.request`      ← `sendChat()`; `chat.delta/done/error` → `onChat(...)`
 * - `session.list.request` ← requested on every hello/reconnect;
 *                            `session.list.response`/`session.tail` → callbacks
 * - `exec.started/output/result` → reduced into `ExecView[]` (`onExec`)
 * - `event` (`catchup.summary`) → validated `onCatchUpSummary` (Learn briefing)
 * - `session.command` ← sealed M1.1 signed control payload (`sendSessionCommand`);
 *   `event` (`session.command.result`) → `onSessionCommandResult`
 * - `todo.request` / `alarm.request` ← correlated CRUD (`requestTodo` /
 *   `requestAlarm`); `todo.response` / `alarm.response` settle the waiter by
 *   `request_id` (same bounded correlation as QQ/memory).
 * - `plan.request` ← correlated 清单/议程 CRUD (`requestPlan`) and assignment
 *   hide/un-hide (`requestAssignment`); `plan.response` settles the waiter by
 *   `request_id` (same bounded correlation as todo).
 *
 * All payloads are the existing `@luminis/protocol` interfaces; nothing is
 * invented here. The controller is deliberately framework-free so it can be
 * tested with a FakeWebSocket (no real Worker/daemon).
 */
import type {
  AlarmManagementOp,
  AlarmRequestPayload,
  ApprovalDecide,
  ApprovalDecision,
  ApprovalStatusItem,
  ApprovalStatusResponse,
  ChatDelta,
  ChatDone,
  ChatError,
  ChatRequest,
  MessageType,
  MemoryManagementOp,
  PlanManagementOp,
  PlanRequestPayload,
  QqManagementOp,
  QqRequestPayload,
  SessionCommandPayload,
  SessionCommandResult,
  SessionListRequest,
  SessionSource,
  SessionTailRequest,
  TodoManagementOp,
  TodoRequestPayload,
} from '@luminis/protocol';
import {
  isAlarmResponsePayload,
  isMemoryResponsePayload,
  isPlanResponsePayload,
  isQqResponsePayload,
  isTodoResponsePayload,
} from '@luminis/protocol';
import { validateDecision } from '../approval';
import { newApprovalNonce, encryptEnvelope, signApproval } from '../crypto/e2e';
import { nowSeconds, randomId } from '../id';
import type { ApprovalView, DeviceIdentityStored } from '../types';
import {
  RelayClient,
  type DeliveryFrame,
  type HelloFrame,
  type RelayClientError,
  type RelayClientOptions,
  type RelayDecodedEvent,
  type RelaySendResult,
  type RelayStatus,
} from '../api/relay';
import { applyExecEvent, approvalViewFromPayload, isRecord, type ExecView } from './reducers';
import { parseCatchUpSummaryPayload, type CatchUpSummary } from './catchup';
import { parseSessionCommandResultEvent } from '../session-control';
import {
  buildSessionTailRequest,
  parseSessionTailErrorEvent,
  type SessionTailError,
} from '../session-history';
import type { AssignmentManagementOp, PlanLiveRequest } from './agenda';

export interface LiveChatDeltaEvent {
  type: 'delta';
  delta: ChatDelta;
}
export interface LiveChatDoneEvent {
  type: 'done';
  done: ChatDone;
}
export interface LiveChatErrorEvent {
  type: 'error';
  error: ChatError;
}
export type LiveChatEvent = LiveChatDeltaEvent | LiveChatDoneEvent | LiveChatErrorEvent;

export interface LiveDeliveryResult {
  /** Envelope id acknowledged by the Worker. */
  id: string;
  ok: boolean;
  /** Present when this envelope was a locally sent approval decision. */
  approval_id?: string;
  error?: string;
}

export interface LiveSendResult {
  ok: boolean;
  /** `true` when the relay accepted the envelope into the local outbox. */
  queued?: boolean;
  envelope_id?: string;
  error?: RelayClientError;
}

/** Daemon-side readable failure for a `session.list.request` (generic `event`). */
export interface SessionListErrorPayload {
  code: string;
  message: string;
}

interface SessionListErrorEvent {
  event: 'session.list.error';
  data: SessionListErrorPayload;
}

/**
 * One session whose tail should be refreshed after a (re)connect. The relay
 * replays unacknowledged envelopes from the last acked `seq` (the transport
 * cursor); the opaque `session.tail` history cursor pages *backwards*, so the
 * resume pull asks for the newest page and the merge dedupes overlaps.
 */
export interface LiveResumeTarget {
  source: SessionSource;
  session_id: string;
}

/** Result of a (re)connect recovery pass, surfaced for UI state clearing. */
export interface LiveRecoveryInfo {
  /** Local seconds when the hello frame was handled. */
  at: number;
  /** Last contiguous Worker queue seq before this connection (replay point). */
  lastSeq: number;
  /** Worker-reported queue depth at hello. */
  queueDepth: number;
  /** Number of `session.tail.request` resume pulls sent. */
  resumed: number;
}

/** Bound on resume pulls per reconnect (phones usually show a handful). */
export const MAX_RESUME_TAILS = 8;
/** Page size for a resume pull (newest subset). */
export const RESUME_TAIL_LIMIT = 50;
/** Bounded wait for a relay management response (round-trip + daemon work). */
export const QQ_REQUEST_TIMEOUT_MS = 10_000;

/** User-facing labels for the correlated management request timeout path. */
const MANAGEMENT_REQUEST_LABELS = {
  'qq.request': 'QQ 管理',
  'memory.request': '记忆管理',
  'todo.request': '待办同步',
  'alarm.request': '闹钟同步',
  'plan.request': '议程同步',
} as const;

/** Result of one correlated relay management request (QQ or memory). */
export interface LiveQqResponse {
  ok: boolean;
  /** `QqStatus`/`{ memories }` for management ops, `{ ok: true }` for clear/disconnect. */
  data?: unknown;
  error?: { code: string; message: string };
}

/** Alias documenting the memory transport's identical result shape. */
export type LiveMemoryResponse = LiveQqResponse;

/** Result of one correlated todo relay request (`todo.response`). */
export type LiveTodoResponse = LiveQqResponse;

/** Result of one correlated alarm relay request (`alarm.response`). */
export type LiveAlarmResponse = LiveQqResponse;

/** Result of one correlated plan/agenda relay request (`plan.response`). */
export type LivePlanResponse = LiveQqResponse;

/** Todo/alarm request fields carried alongside the operation (v0.6.0). */
export interface LiveTodoAlarmRequest {
  /** Required by update/delete/toggle|fired; omitted for list/create. */
  id?: string;
  /** Required by create/update; omitted for the other ops. */
  body?: Record<string, unknown>;
}

interface PendingRelayRequest {
  resolve(response: LiveQqResponse): void;
  timer: ReturnType<typeof setTimeout>;
  envelopeId?: string;
}

export interface LiveRelayControllerOptions {
  url: string;
  token: string;
  daemonDeviceId: string;
  identity: DeviceIdentityStored;
  /** Derived AES-256-GCM channel key (X25519 handshake). Required. */
  channelKey: CryptoKey;
  onApproval(view: ApprovalView): void;
  onSessionListResponse(payload: unknown): void;
  onSessionTail(payload: unknown): void;
  onChat(event: LiveChatEvent): void;
  onExec(state: ExecView[], changed: ExecView | null): void;
  onApprovalStatus(payload: ApprovalStatusResponse): void;
  /** Validated Learn catch-up briefing (`event: catchup.summary`). */
  onCatchUpSummary(summary: CatchUpSummary): void;
  /** Daemon could not answer the session list (`event: session.list.error`). */
  onSessionListError?(payload: SessionListErrorPayload): void;
  /** Validated M1.1 `event: session.command.result` payload. */
  onSessionCommandResult(result: SessionCommandResult): void;
  /** Historical page pull answer (`session.tail.response`, `SessionTail` shape). */
  onSessionTailResponse?(payload: unknown): void;
  /** Historical page pull failure (`event: session.tail.error`). */
  onSessionTailError?(error: SessionTailError): void;
  /**
   * Sessions to re-pull after every hello (initial connect and reconnect).
   * Read lazily so the app always resumes from its current cursor/state.
   */
  getResumeTargets?(): readonly LiveResumeTarget[];
  /** Fired once per hello, after `session.list.request` + resume pulls. */
  onRecovery?(info: LiveRecoveryInfo): void;
  onDelivery(result: LiveDeliveryResult): void;
  onStatus?(status: RelayStatus, detail?: string): void;
  onError?(error: RelayClientError): void;
  onAuthError?(error: RelayClientError): void;
  /** Bounded auto-reconnect attempts were exhausted (UI should offer retry). */
  onReconnectExhausted?(): void;
  /** Injected relay constructor options for tests (fake WS / timers). */
  relayOptions?: Partial<
    Pick<
      RelayClientOptions,
      | 'webSocketFactory'
      | 'WebSocketImpl'
      | 'scheduler'
      | 'random'
      | 'clock'
      | 'reconnect'
      | 'outboxLimit'
      | 'baseBackoffMs'
      | 'maxBackoffMs'
      | 'jitterMs'
      | 'resyncDelayMs'
      | 'maxReconnectAttempts'
      | 'connectTimeoutMs'
    >
  >;
  now?: () => number;
}

function isChatDelta(payload: unknown): payload is ChatDelta {
  if (!isRecord(payload)) return false;
  return (
    typeof payload.message_id === 'string' &&
    typeof payload.seq === 'number' &&
    (payload.kind === 'content' || payload.kind === 'reasoning') &&
    typeof payload.delta === 'string'
  );
}

function isChatDone(payload: unknown): payload is ChatDone {
  if (!isRecord(payload)) return false;
  const model = payload.model;
  return (
    typeof payload.message_id === 'string' &&
    isRecord(model) &&
    typeof model.provider === 'string' &&
    typeof model.model === 'string'
  );
}

function isChatError(payload: unknown): payload is ChatError {
  if (!isRecord(payload)) return false;
  return typeof payload.message === 'string' && typeof payload.code === 'string';
}

function isSessionListErrorEvent(payload: unknown): payload is SessionListErrorEvent {
  if (!isRecord(payload) || payload['event'] !== 'session.list.error') return false;
  const data = payload['data'];
  if (!isRecord(data)) return false;
  return typeof data['code'] === 'string' && typeof data['message'] === 'string';
}

function isApprovalStatusItem(value: unknown): value is ApprovalStatusItem {
  return (
    isRecord(value) &&
    typeof value['approval_id'] === 'string' &&
    typeof value['status'] === 'string' &&
    ['pending', 'granted', 'denied', 'expired', 'revoked'].includes(value['status'])
  );
}

function isApprovalStatusResponse(payload: unknown): payload is ApprovalStatusResponse {
  return (
    isRecord(payload) &&
    Array.isArray(payload['approvals']) &&
    payload['approvals'].every(isApprovalStatusItem)
  );
}

export class LiveRelayController {
  private readonly options: LiveRelayControllerOptions;
  private readonly relay: RelayClient;
  private readonly clock: () => number;
  private readonly pendingDecisions = new Map<string, string>();
  private readonly pendingRequests = new Map<string, PendingRelayRequest>();
  private execs: ExecView[] = [];
  private closed = false;

  constructor(options: LiveRelayControllerOptions) {
    if (!options.channelKey) {
      throw new Error('live relay 需要 E2E 通道密钥（crypto.subtle 派生）');
    }
    this.options = options;
    this.clock = options.now ?? nowSeconds;
    const deviceId = options.identity.device_id;
    this.relay = new RelayClient({
      url: options.url,
      token: options.token,
      role: 'phone',
      e2e: { channel: { key: options.channelKey, deviceId } },
      ...(options.relayOptions ?? {}),
      onStatus: (status, detail) => options.onStatus?.(status, detail),
      onHello: (frame) => {
        // Recovery on every (re)connect/resync: refresh the read-only session
        // snapshot, re-pull the newest tail page for sessions already on screen
        // (the Worker replays unacked envelopes from `acked_seq`), then let the
        // app clear stale readiness state.
        void this.recover(frame);
      },
      onReconnectExhausted: () => options.onReconnectExhausted?.(),
      onEvent: (event) => this.handleEvent(event),
      onDelivery: (frame) => this.handleDelivery(frame),
      onError: (error) => options.onError?.(error),
      onAuthError: (error) => options.onAuthError?.(error),
    });
  }

  get status(): RelayStatus {
    return this.relay.status;
  }

  get outboxSize(): number {
    return this.relay.outboxSize;
  }

  /** Automatic retries are suppressed after a token rejection/revocation. */
  get authFailed(): boolean {
    return this.relay.authFailedFatal;
  }

  /** Consecutive auto-reconnect attempts since the last successful open. */
  get reconnectAttempts(): number {
    return this.relay.reconnectAttempts;
  }

  /** Executions observed in this session (bounded, newest first). */
  get execViews(): readonly ExecView[] {
    return this.execs;
  }

  connect(): void {
    if (this.closed) return;
    this.relay.connect();
  }

  /**
   * Force an immediate reconnect attempt (browser `online`/`visibilitychange`
   * or the user retry button). Safe to call repeatedly: it never opens two
   * sockets, and `resetAttempts` restores the bounded retry budget.
   */
  reconnectNow(options: { resetAttempts?: boolean; force?: boolean } = {}): void {
    if (this.closed) return;
    this.relay.reconnectNow(options);
  }

  close(): void {
    this.closed = true;
    this.relay.close();
    this.rejectPendingRequests();
  }

  dispose(): void {
    this.closed = true;
    this.relay.dispose();
    this.pendingDecisions.clear();
    this.rejectPendingRequests();
  }

  /**
   * Request one QQ management operation from the daemon over the E2E relay
   * (v0.5.0 live management card). The request/response is correlated by a
   * random `request_id`; the wait is bounded and a failed delivery rejects
   * immediately instead of hanging. No optimistic result is ever returned.
   */
  async requestQq(
    op: QqManagementOp,
    body?: Record<string, unknown>,
    options: { timeoutMs?: number } = {},
  ): Promise<LiveQqResponse> {
    const requestId = randomId('qq_');
    const payload: QqRequestPayload = {
      request_id: requestId,
      op,
      ...(body === undefined ? {} : { body }),
    };
    return this.requestCorrelated('qq.request', requestId, payload, options.timeoutMs);
  }

  /**
   * Request one long-term memory operation from the daemon over the E2E relay
   * (v0.5.0 live Settings). Same bounded correlation/reconnect safety as QQ
   * management; responses are re-validated by the caller via lib/memory.
   */
  async requestMemory(
    op: MemoryManagementOp,
    id?: string,
    options: { timeoutMs?: number } = {},
  ): Promise<LiveMemoryResponse> {
    const requestId = randomId('mem_');
    return this.requestCorrelated(
      'memory.request',
      requestId,
      {
        request_id: requestId,
        op,
        ...(id === undefined ? {} : { id }),
      },
      options.timeoutMs,
    );
  }

  /**
   * Request one todo operation from the daemon over the E2E relay (v0.6.0).
   * Correlated by a random `request_id`; the bounded wait rejects on delivery
   * failure instead of hanging. The caller re-validates `data`.
   */
  async requestTodo(
    op: TodoManagementOp,
    request: LiveTodoAlarmRequest = {},
    options: { timeoutMs?: number } = {},
  ): Promise<LiveTodoResponse> {
    const requestId = randomId('todo_');
    const payload: TodoRequestPayload = {
      request_id: requestId,
      op,
      ...(request.id === undefined ? {} : { id: request.id }),
      ...(request.body === undefined ? {} : { body: request.body }),
    };
    return this.requestCorrelated('todo.request', requestId, payload, options.timeoutMs);
  }

  /** Request one alarm operation from the daemon over the E2E relay (v0.6.0). */
  async requestAlarm(
    op: AlarmManagementOp,
    request: LiveTodoAlarmRequest = {},
    options: { timeoutMs?: number } = {},
  ): Promise<LiveAlarmResponse> {
    const requestId = randomId('alarm_');
    const payload: AlarmRequestPayload = {
      request_id: requestId,
      op,
      ...(request.id === undefined ? {} : { id: request.id }),
      ...(request.body === undefined ? {} : { body: request.body }),
    };
    return this.requestCorrelated('alarm.request', requestId, payload, options.timeoutMs);
  }

  /**
   * Request one plans/agenda operation from the daemon over the E2E relay
   * (v0.6.0). Mirrors `/plans` + `/agenda`: `agenda` data is re-validated by the
   * caller (lib/live/agenda), and the bounded wait rejects on delivery failure
   * instead of hanging. Response data is never trusted or fabricated here.
   */
  async requestPlan(
    op: PlanManagementOp,
    request: PlanLiveRequest = {},
    options: { timeoutMs?: number } = {},
  ): Promise<LivePlanResponse> {
    const requestId = randomId('plan_');
    const payload: PlanRequestPayload = {
      request_id: requestId,
      op,
      ...(request.id === undefined ? {} : { id: request.id }),
      ...(request.body === undefined ? {} : { body: request.body }),
      ...(request.date === undefined ? {} : { date: request.date }),
    };
    return this.requestCorrelated('plan.request', requestId, payload, options.timeoutMs);
  }

  /**
   * Hide/un-hide one 网络学堂 assignment row from 议程 (v0.6.0).
   *
   * Uses the protocol `dismiss_assignment`/`restore_assignment` ops: the payload
   * is `{request_id, op, id}` (no body/date) and the daemon persists the
   * dismissal locally — Learn is never touched.
   */
  async requestAssignment(
    op: AssignmentManagementOp,
    id: string,
    options: { timeoutMs?: number } = {},
  ): Promise<LivePlanResponse> {
    const requestId = randomId('plan_');
    const payload: PlanRequestPayload = { request_id: requestId, op, id };
    return this.requestCorrelated('plan.request', requestId, payload, options.timeoutMs);
  }

  /**
   * Send a management request, register its bounded pending waiter and resolve
   * it from the matching `*.response` envelope. A rejected delivery settles the
   * waiter immediately; close/dispose rejects all waiters.
   */
  private async requestCorrelated(
    type: 'qq.request' | 'memory.request' | 'todo.request' | 'alarm.request' | 'plan.request',
    requestId: string,
    payload: unknown,
    timeoutMsOption?: number,
  ): Promise<LiveQqResponse> {
    if (this.closed) {
      return { ok: false, error: { code: 'CONFLICT', message: 'relay 控制器已关闭' } };
    }
    const timeoutMs = Math.min(60_000, Math.max(1_000, timeoutMsOption ?? QQ_REQUEST_TIMEOUT_MS));
    const label = MANAGEMENT_REQUEST_LABELS[type];
    const responsePromise = new Promise<LiveQqResponse>((resolve) => {
      const timer = setTimeout(() => {
        if (!this.pendingRequests.has(requestId)) return;
        this.pendingRequests.delete(requestId);
        resolve({ ok: false, error: { code: 'EXEC_TIMEOUT', message: `${label}请求超时` } });
      }, timeoutMs);
      this.pendingRequests.set(requestId, { resolve, timer });
    });
    const sent = await this.sealAndSend(type, payload);
    if (!sent.ok) {
      this.settleRequest(requestId, {
        ok: false,
        ...(sent.error !== undefined
          ? { error: { code: sent.error.code, message: sent.error.message } }
          : { error: { code: 'INTERNAL', message: `${label}请求发送失败` } }),
      });
      return responsePromise;
    }
    if (sent.envelope_id !== undefined) {
      const pending = this.pendingRequests.get(requestId);
      if (pending) pending.envelopeId = sent.envelope_id;
    }
    return responsePromise;
  }

  /**
   * Sign + E2E-seal an `approval.decide` payload and queue/send it to the
   * daemon. Client-side approval rules are enforced before signing.
   * `signed_at` is conveyed by `Envelope.ts` (the protocol `ApprovalDecide`
   * interface has no separate timestamp field).
   */
  async decideApproval(
    approval: ApprovalView,
    decision: ApprovalDecision,
    options: { confirmHighRisk: boolean; nonce?: string },
  ): Promise<LiveSendResult> {
    const validation = validateDecision(approval, {
      decision,
      args_hash: approval.args_hash,
      confirm_high_risk: options.confirmHighRisk,
    });
    if (!validation.ok) {
      return { ok: false, error: { code: validation.code, message: validation.message } };
    }
    const nonce = options.nonce ?? newApprovalNonce();
    let signature: string;
    try {
      signature = await signApproval(this.options.identity.signing_private_key, {
        approval_id: approval.approval_id,
        decision,
        args_hash: approval.args_hash,
        nonce,
      });
    } catch (error) {
      return {
        ok: false,
        error: {
          code: 'AUTH_INVALID',
          message: `审批签名失败：${error instanceof Error ? error.message : '未知错误'}`,
        },
      };
    }
    const payload: ApprovalDecide = {
      approval_id: approval.approval_id,
      decision,
      signature,
      nonce,
      args_hash: approval.args_hash,
    };
    const sealed = await this.sealAndSend('approval.decide', payload);
    if (sealed.ok && sealed.envelope_id) {
      this.pendingDecisions.set(sealed.envelope_id, approval.approval_id);
    }
    return sealed;
  }

  /** Send a chat turn through the daemon over the E2E relay. */
  async sendChat(request: ChatRequest): Promise<LiveSendResult> {
    return this.sealAndSend('chat.request', request);
  }

  /** Recover current approval statuses (used after refresh/reconnect). */
  async requestApprovalStatus(approvalIds: string[]): Promise<LiveSendResult> {
    const ids = [...new Set(approvalIds)].slice(0, 50);
    if (ids.length === 0) return { ok: true };
    return this.sealAndSend('approval.status.request', { approval_ids: ids });
  }

  /** Ask the daemon for the current read-only session list. */
  async requestSessions(query: SessionListRequest = {}): Promise<LiveSendResult> {
    return this.sealAndSend('session.list.request', query);
  }

  /**
   * Request one older page of session events (v0.3.0 history pull). The daemon
   * answers with `session.tail.response` or `event: session.tail.error`; the
   * caller correlates by source/session_id (one request at a time per session).
   */
  async requestSessionTail(request: SessionTailRequest): Promise<LiveSendResult> {
    return this.sealAndSend('session.tail.request', request);
  }

  /**
   * Send an M1.1 signed `session.command` (caller already built/validated the
   * payload with `buildSignedSessionCommand`). The daemon may still reject it
   * (off/unsupported); that arrives as a `session.command.result` event.
   */
  async sendSessionCommand(payload: SessionCommandPayload): Promise<LiveSendResult> {
    return this.sealAndSend('session.command', payload);
  }

  // -- internals ------------------------------------------------------------

  /**
   * Post-handshake recovery (runs on the initial connect and on every relay
   * reconnect/resync):
   *
   * 1. re-request the read-only session snapshot (`session.list`);
   * 2. re-pull the newest tail page for the sessions the app already shows,
   *    bounded and deduped (the Worker replays unacked envelopes from the last
   *    acked seq, so this only fills gaps the queue no longer holds);
   * 3. report the recovery so the UI clears stale “未就绪” state.
   *
   * Failures are non-fatal: the next reconnect retries, and encrypted payloads
   * are never logged.
   */
  private async recover(frame: HelloFrame): Promise<void> {
    const list = await this.requestSessions();
    if (this.closed) return;
    if (!list.ok && list.error) this.options.onError?.(list.error);

    let resumed = 0;
    const seen = new Set<string>();
    for (const target of this.options.getResumeTargets?.() ?? []) {
      if (resumed >= MAX_RESUME_TAILS) break;
      const key = `${target.source}\u0000${target.session_id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const request = buildSessionTailRequest({
        source: target.source,
        sessionId: target.session_id,
        limit: RESUME_TAIL_LIMIT,
      });
      if (!request) continue;
      const sent = await this.requestSessionTail(request);
      if (this.closed) return;
      if (sent.ok) resumed += 1;
    }

    this.options.onRecovery?.({
      at: this.clock(),
      lastSeq: this.relay.lastSeq,
      queueDepth: typeof frame.queue_depth === 'number' ? frame.queue_depth : 0,
      resumed,
    });
  }

  private async sealAndSend(type: MessageType, payload: unknown): Promise<LiveSendResult> {
    if (this.closed) {
      return {
        ok: false,
        error: { code: 'CONFLICT', message: 'relay 控制器已关闭', retryable: false },
      };
    }
    try {
      const envelope = await encryptEnvelope({
        type,
        payload,
        key: this.options.channelKey,
        deviceId: this.options.identity.device_id,
      });
      const result: RelaySendResult = this.relay.sendEnvelope(
        this.options.daemonDeviceId,
        envelope,
      );
      if (!result.ok) {
        return { ok: false, ...(result.error ? { error: result.error } : {}) };
      }
      return {
        ok: true,
        ...(result.queued !== undefined ? { queued: result.queued } : {}),
        envelope_id: envelope.id,
      };
    } catch (error) {
      return {
        ok: false,
        error: {
          code: 'INTERNAL',
          message: `E2E 加密失败：${error instanceof Error ? error.message : '未知错误'}`,
          retryable: false,
        },
      };
    }
  }

  private settleRequest(requestId: string, response: LiveQqResponse): void {
    const pending = this.pendingRequests.get(requestId);
    if (pending === undefined) return;
    this.pendingRequests.delete(requestId);
    clearTimeout(pending.timer);
    pending.resolve(response);
  }

  /** Fail all in-flight management requests (controller close/dispose). */
  private rejectPendingRequests(): void {
    if (this.pendingRequests.size === 0) return;
    for (const [requestId, pending] of this.pendingRequests) {
      clearTimeout(pending.timer);
      pending.resolve({ ok: false, error: { code: 'CONFLICT', message: 'relay 控制器已关闭' } });
      this.pendingRequests.delete(requestId);
    }
  }

  /** Settle a validated `qq.response`/`memory.response` by request id. */
  private handleManagementResponse(payload: {
    request_id: string;
    ok: boolean;
    data?: unknown;
    error?: { code: string; message: string };
  }): void {
    this.settleRequest(payload.request_id, {
      ok: payload.ok,
      ...(payload.data === undefined ? {} : { data: payload.data }),
      ...(payload.error === undefined
        ? {}
        : { error: { code: payload.error.code, message: payload.error.message } }),
    });
  }

  private handleEvent(event: RelayDecodedEvent): void {
    switch (event.type) {
      case 'approval.request': {
        const view = approvalViewFromPayload(event.payload, this.clock());
        if (view) this.options.onApproval(view);
        break;
      }
      case 'approval.status.response':
        if (isApprovalStatusResponse(event.payload)) {
          this.options.onApprovalStatus(event.payload);
        }
        break;
      case 'session.list.response':
        this.options.onSessionListResponse(event.payload);
        break;
      case 'session.tail':
        this.options.onSessionTail(event.payload);
        break;
      case 'session.tail.response':
        this.options.onSessionTailResponse?.(event.payload);
        break;
      case 'qq.response': {
        if (isQqResponsePayload(event.payload)) {
          this.handleManagementResponse(event.payload);
        }
        break;
      }
      case 'memory.response': {
        if (isMemoryResponsePayload(event.payload)) {
          this.handleManagementResponse(event.payload);
        }
        break;
      }
      case 'todo.response': {
        if (isTodoResponsePayload(event.payload)) {
          this.handleManagementResponse(event.payload);
        }
        break;
      }
      case 'alarm.response': {
        if (isAlarmResponsePayload(event.payload)) {
          this.handleManagementResponse(event.payload);
        }
        break;
      }
      case 'plan.response': {
        if (isPlanResponsePayload(event.payload)) {
          this.handleManagementResponse(event.payload);
        }
        break;
      }
      case 'chat.delta':
        if (isChatDelta(event.payload)) {
          this.options.onChat({ type: 'delta', delta: event.payload });
        }
        break;
      case 'chat.done':
        if (isChatDone(event.payload)) {
          this.options.onChat({ type: 'done', done: event.payload });
        }
        break;
      case 'chat.error':
        if (isChatError(event.payload)) {
          this.options.onChat({ type: 'error', error: event.payload });
        }
        break;
      case 'exec.started':
      case 'exec.output':
      case 'exec.result': {
        const reduced = applyExecEvent(this.execs, event.type, event.payload, this.clock());
        if (reduced.changed) {
          this.execs = reduced.state;
          this.options.onExec(this.execs, reduced.changed);
        }
        break;
      }
      case 'event': {
        // Generic events ride the existing MessageType. Session-list failures get
        // a readable UI state; malformed/foreign events are ignored.
        if (isSessionListErrorEvent(event.payload)) {
          this.options.onSessionListError?.({
            code: event.payload.data.code,
            message: event.payload.data.message.slice(0, 300),
          });
          break;
        }
        const commandResult = parseSessionCommandResultEvent(event.payload);
        if (commandResult) {
          this.options.onSessionCommandResult(commandResult);
          break;
        }
        const tailError = parseSessionTailErrorEvent(event.payload);
        if (tailError) {
          this.options.onSessionTailError?.(tailError);
          break;
        }
        const summary = parseCatchUpSummaryPayload(event.payload);
        if (summary) this.options.onCatchUpSummary(summary);
        break;
      }
      default:
        break;
    }
  }

  private handleDelivery(frame: DeliveryFrame): void {
    const approvalId = this.pendingDecisions.get(frame.id);
    if (approvalId) this.pendingDecisions.delete(frame.id);
    this.options.onDelivery({
      id: frame.id,
      ok: frame.ok,
      ...(approvalId ? { approval_id: approvalId } : {}),
      ...(frame.error?.message ? { error: frame.error.message } : {}),
    });
    if (frame.ok) return;
    // A rejected management envelope can never produce a response: settle the
    // correlated request now instead of waiting for the timeout.
    for (const [requestId, pending] of this.pendingRequests) {
      if (pending.envelopeId !== frame.id) continue;
      this.settleRequest(requestId, {
        ok: false,
        error: { code: 'CONFLICT', message: '管理请求发送失败' },
      });
      break;
    }
  }
}
